import axios from 'axios';

const API_BASE = '/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Interceptor: cuando se envía FormData, eliminar Content-Type
// para que axios genere automáticamente multipart/form-data con boundary
api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Restaurar token si existe en localStorage
const token = localStorage.getItem('token');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

const sucursalActiva = localStorage.getItem('sucursalActiva');
if (sucursalActiva) {
  api.defaults.headers.common['X-Sucursal-Id'] = sucursalActiva;
}

// FIX: getImageUrl() no longer exposes JWT in URL query parameters.
// Public images (jpg, png, gif, webp, svg, mp4, 3gp) are served without auth.
// Protected files (pdf, p12, comprobantes, facturas-compra) must be fetched
// via fetchProtectedFile() which uses Authorization header instead.
export const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;

  // Convertir /uploads/ legacy a /cdn/
  let cdnUrl = url;
  if (url.startsWith('/uploads/')) {
    const filename = url.replace('/uploads/', '');
    cdnUrl = `/cdn/${filename}`;
  } else if (!url.startsWith('/cdn/')) {
    cdnUrl = `/cdn/${url}`;
  }

  // FIX: No longer appending ?token= to any URL.
  // Public images are served without auth by the CDN backend.
  // Protected files should use fetchProtectedFile() instead.
  return cdnUrl;
};

// FIX: Helper to check if a CDN path requires authentication.
// Protected paths: /cdn/comprobantes/, /cdn/facturas-compra/, /cdn/movimiento-comprobantes/
// Protected extensions: .pdf, .p12
export const isProtectedCdnPath = (cdnUrl) => {
  if (!cdnUrl) return false;
  const protectedPaths = ['/cdn/comprobantes/', '/cdn/facturas-compra/', '/cdn/movimiento-comprobantes/'];
  const protectedExtensions = ['.pdf', '.p12'];
  return protectedPaths.some(p => cdnUrl.startsWith(p)) ||
    protectedExtensions.some(ext => cdnUrl.toLowerCase().endsWith(ext));
};

// FIX: Fetch protected CDN files using Authorization header instead of token in URL.
// Returns an object URL (blob URL) that can be used in <img src>, <a href>, etc.
// Caller is responsible for revoking the object URL when done (URL.revokeObjectURL).
export const fetchProtectedFile = async (cdnPath) => {
  if (!cdnPath) return null;
  const url = getImageUrl(cdnPath);
  const response = await api.get(url, {
    baseURL: '', // CDN paths are absolute (e.g., /cdn/comprobantes/file.pdf)
    responseType: 'blob'
  });
  return URL.createObjectURL(response.data);
};

// ─── APIs EXISTENTES (misma interfaz, sin romper nada) ───

export const productosAPI = {
  getAll: (params) => api.get('/productos', { params }),
  getById: (id) => api.get(`/productos/${id}`),
  create: (data) => api.post('/productos', data),
  update: (id, data) => api.put(`/productos/${id}`, data),
  delete: (id) => api.delete(`/productos/${id}`),
  // FIX: Accept optional config to support AbortController signal for race condition prevention
  verificarCodigo: (codigo, excludeId, config = {}) => api.get(`/productos/verificar-codigo/${codigo}`, { params: { excludeId }, ...config }),
  getSiguienteCodigo: () => api.get('/productos/siguiente-codigo'),
  getTipos: () => api.get('/productos/tipos'),
  getGeneros: () => api.get('/productos/generos')
};

export const categoriasAPI = {
  getAll: () => api.get('/categorias'),
  getById: (id) => api.get(`/categorias/${id}`),
  create: (data) => api.post('/categorias', data),
  update: (id, data) => api.put(`/categorias/${id}`, data),
  delete: (id) => api.delete(`/categorias/${id}`)
};

export const marcasAPI = {
  getAll: () => api.get('/marcas'),
  getById: (id) => api.get(`/marcas/${id}`),
  create: (data) => api.post('/marcas', data),
  update: (id, data) => api.put(`/marcas/${id}`, data),
  delete: (id) => api.delete(`/marcas/${id}`)
};

export const proveedoresAPI = {
  getAll: (params) => api.get('/proveedores', { params }),
  getById: (id) => api.get(`/proveedores/${id}`),
  create: (data) => api.post('/proveedores', data),
  update: (id, data) => api.put(`/proveedores/${id}`, data),
  delete: (id) => api.delete(`/proveedores/${id}`)
};

export const comprasAPI = {
  getAll: (params) => api.get('/compras', { params }),
  getById: (id) => api.get(`/compras/${id}`),
  create: (data) => api.post('/compras', data),
  confirmar: (id) => api.post(`/compras/${id}/confirmar`),
  cancelar: (id, data) => api.post(`/compras/${id}/cancelar`, data),
  // Pagos
  registrarPago: (id, data) => api.post(`/compras/${id}/pagar`, data),
  getCuentasPorPagar: (params) => api.get('/compras/cuentas-por-pagar', { params }),
  // Facturas (comprobantes del proveedor)
  getFacturas: (id) => api.get(`/compras/${id}/factura`),
  uploadFactura: (id, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('facturas', f));
    return api.post(`/compras/${id}/factura`, formData);
  },
  deleteFactura: (id, comprobanteId) => api.delete(`/compras/${id}/factura/${comprobanteId}`)
};

export const inventarioAPI = {
  getAll: (params) => api.get('/inventario', { params }),
  getKardex: (productoId, params) => api.get(`/inventario/kardex/${productoId}`, { params }),
  ajustarStock: (productoId, data) => api.post(`/inventario/ajuste/${productoId}`, data)
};

export const configuracionAPI = {
  getAll: () => api.get('/configuracion'),
  update: (clave, valor) => api.put(`/configuracion/${clave}`, { valor }),
  updateBulk: (data) => api.post('/configuracion/bulk', data)
};

export const reportesAPI = {
  getDashboard: () => api.get('/reportes/dashboard'),
  exportarExcel: (params) => api.get('/reportes/inventario/excel', { params, responseType: 'blob' }),
  exportarPDF: (params) => api.get('/reportes/inventario/pdf', { params, responseType: 'blob' }),
  exportarComprasExcel: (params) => api.get('/reportes/compras/excel', { params, responseType: 'blob' }),
  exportarComprasPDF: (params) => api.get('/reportes/compras/pdf', { params, responseType: 'blob' }),
  // Ventas
  exportarVentasExcel: (params) => api.get('/reportes/ventas/excel', { params, responseType: 'blob' }),
  exportarVentasPDF: (params) => api.get('/reportes/ventas/pdf', { params, responseType: 'blob' }),
  // Cuentas por Cobrar
  exportarCxCExcel: (params) => api.get('/reportes/cxc/excel', { params, responseType: 'blob' }),
  exportarCxCPDF: (params) => api.get('/reportes/cxc/pdf', { params, responseType: 'blob' }),
  // Gastos / Egresos
  exportarGastosExcel: (params) => api.get('/reportes/gastos/excel', { params, responseType: 'blob' }),
  exportarGastosPDF: (params) => api.get('/reportes/gastos/pdf', { params, responseType: 'blob' }),
  // Reportes Profesionales
  getVentasDiarias: (params) => api.get('/reportes/ventas-diarias', { params }),
  getConciliacionCaja: (params) => api.get('/reportes/conciliacion-caja', { params }),
  getAgingCxC: (params) => api.get('/reportes/aging-cxc', { params }),
  getEstadoResultados: (params) => api.get('/reportes/estado-resultados', { params }),
  getReporteIVA: (params) => api.get('/reportes/iva', { params }),
  exportarVentasDiariasExcel: (params) => api.get('/reportes/ventas-diarias/excel', { params, responseType: 'blob' }),
  exportarConciliacionExcel: (params) => api.get('/reportes/conciliacion-caja/excel', { params, responseType: 'blob' }),
  exportarAgingExcel: (params) => api.get('/reportes/aging-cxc/excel', { params, responseType: 'blob' }),
  exportarEstadoResultadosExcel: (params) => api.get('/reportes/estado-resultados/excel', { params, responseType: 'blob' }),
  exportarIVAExcel: (params) => api.get('/reportes/iva/excel', { params, responseType: 'blob' })
};

export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData);
  },
  uploadVideo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/video', formData, {
      timeout: 120000 // 2 min para videos grandes
    });
  },
  delete: (filename) => api.delete(`/upload/${filename}`)
};

// ─── NUEVAS APIs ───

export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  me: () => api.get('/auth/me'),
  cambiarPassword: (passwordActual, passwordNuevo) =>
    api.put('/auth/cambiar-password', { passwordActual, passwordNuevo })
};

export const usuariosAPI = {
  getAll: (params) => api.get('/usuarios', { params }),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  getRoles: () => api.get('/usuarios/roles')
};

// Actividad del Equipo (supervisión: ventas, servicios, abonos y órdenes por usuario)
export const actividadAPI = {
  getFeed: (params) => api.get('/actividad', { params }),
  getResumen: (params) => api.get('/actividad/resumen', { params }),
  exportarExcel: (params) => api.get('/actividad/export', { params, responseType: 'blob' }),
  // Corrige SOLO el N° de orden física de un registro (admin/gerente, auditado).
  actualizarNumeroOrden: (data) => api.patch('/actividad/numero-orden', data)
};

export const sucursalesAPI = {
  getAll: (params) => api.get('/sucursales', { params }),
  getById: (id) => api.get(`/sucursales/${id}`),
  create: (data) => api.post('/sucursales', data),
  update: (id, data) => api.put(`/sucursales/${id}`, data),
  // Stock inter-sucursal
  getStock: (sucursalId, params) => api.get(`/sucursales/${sucursalId}/stock`, { params }),
  getStockTodas: (productoId) => api.get(`/sucursales/1/stock`, { params: { producto_id: productoId, ver_todas: true } }),
  // Transferencias entre sucursales
  getTransferencias: (params) => api.get('/sucursales/transferencias/lista', { params }),
  getTransferenciaDetalle: (id) => api.get(`/sucursales/transferencias/${id}`),
  crearTransferencia: (data) => api.post('/sucursales/transferencias', data),
  completarTransferencia: (id, data) => api.put(`/sucursales/transferencias/${id}/completar`, data),
  rechazarTransferencia: (id, data) => api.put(`/sucursales/transferencias/${id}/rechazar`, data),
  cancelarTransferencia: (id) => api.put(`/sucursales/transferencias/${id}/cancelar`),
  marcarEnTransito: (id) => api.put(`/sucursales/transferencias/${id}/en-transito`)
};

// ─── Fase 2: Clientes + Historia Clinica ───

export const clientesAPI = {
  getAll: (params) => api.get('/clientes', { params }),
  getById: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  buscarPorCI: (ci) => api.get('/clientes/buscar', { params: { ci } }),
  buscarOrden: (q) => api.get('/clientes/buscar-orden', { params: { q } }),
  validarCI: (data) =>
    api.post('/clientes/validar-ci', typeof data === 'string' ? { identificacion: data } : data)
};

export const historiasClinicasAPI = {
  getAll: (params) => api.get('/historias-clinicas', { params }),
  getById: (id) => api.get(`/historias-clinicas/${id}`),
  create: (data) => api.post('/historias-clinicas', data),
  update: (id, data) => api.put(`/historias-clinicas/${id}`, data),
  getComparativa: (clienteId) => api.get(`/historias-clinicas/cliente/${clienteId}/comparativa`),
  exportPdf: (data) => api.post('/historias-clinicas/export-pdf', data, { responseType: 'blob' })
};

export const prescripcionesAPI = {
  getByCliente: (clienteId) => api.get(`/prescripciones/cliente/${clienteId}`)
};

// ─── Fase 3: Ventas + Caja ───

export const ventasAPI = {
  getAll: (params) => api.get('/ventas', { params }),
  getById: (id) => api.get(`/ventas/${id}`),
  create: (data) => api.post('/ventas', data),
  anular: (id, data) => api.post(`/ventas/${id}/anular`, data),
  registrarAbono: (id, data) => api.post(`/ventas/${id}/abono`, data),
  // Agregar/quitar ítems de una cuenta por cobrar (admin/gerente). `previsualizar: true` no guarda.
  editarItems: (id, data) => api.put(`/ventas/${id}/items`, data),
  getReporte: (params) => api.get('/ventas/reporte', { params })
};

export const cajaAPI = {
  getActiva: () => api.get('/caja/activa'),
  abrir: (data) => api.post('/caja/abrir', data),
  cerrar: (data) => api.post('/caja/cerrar', data),
  registrarMovimiento: (data) => api.post('/caja/movimiento', data),
  uploadMovimientoComprobante: (movimientoId, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('comprobantes', f));
    return api.post(`/caja/movimiento/${movimientoId}/comprobantes`, formData);
  },
  getMovimientos: (params) => api.get('/caja/movimientos', { params }),
  getHistorial: (params) => api.get('/caja/historial', { params }),
  getCajeros: () => api.get('/caja/cajeros'),
  validarCierre: (params) => api.get('/caja/validar-cierre', { params })
};

// ─── Periodos Diarios (Multi-Cajero) ───

export const periodosAPI = {
  getActivo: () => api.get('/periodos/activo'),
  abrir: (data) => api.post('/periodos/abrir', data),
  cerrar: (data) => api.post('/periodos/cerrar', data),
  getCajasAbiertas: () => api.get('/periodos/cajas-abiertas'),
  getHistorial: (params) => api.get('/periodos/historial', { params }),
  getResumen: (id) => api.get(`/periodos/${id}/resumen`)
};

// ─── Fase 4: Órdenes de Trabajo ───

export const ordenesTrabajoAPI = {
  getAll: (params) => api.get('/ordenes-trabajo', { params }),
  getById: (id) => api.get(`/ordenes-trabajo/${id}`),
  create: (data) => api.post('/ordenes-trabajo', data),
  update: (id, data) => api.put(`/ordenes-trabajo/${id}`, data),
  cambiarEstado: (id, data) => api.post(`/ordenes-trabajo/${id}/estado`, data),
  getResumen: () => api.get('/ordenes-trabajo/resumen')
};

// ─── Fase 5: Facturación Electrónica SRI ───

export const facturacionAPI = {
  getAll: (params) => api.get('/facturacion', { params }),
  getById: (id) => api.get(`/facturacion/${id}`),
  generarFactura: (ventaId) => api.post(`/facturacion/generar/${ventaId}`),
  procesoCompleto: (ventaId) => api.post(`/facturacion/proceso-completo/${ventaId}`),
  enviar: (id) => api.post(`/facturacion/${id}/enviar`),
  consultar: (id) => api.post(`/facturacion/${id}/consultar`),
  descargarRIDE: (id) => api.get(`/facturacion/${id}/ride`, { responseType: 'blob' }),
  descargarXML: (id) => api.get(`/facturacion/${id}/xml`, { responseType: 'blob' }),
  reenviarEmail: (id, data) => api.post(`/facturacion/${id}/reenviar-email`, data),
  testEmail: () => api.post('/facturacion/test-email'),
  uploadCertificado: (file, password) => {
    const formData = new FormData();
    formData.append('certificado', file);
    formData.append('password', password);
    return api.post('/facturacion/certificado/upload', formData);
  },
  getCertificadoInfo: () => api.get('/facturacion/certificado/info'),
  updateCertPassword: (password) => api.put('/facturacion/certificado/password', { password }),
  testConexion: () => api.post('/facturacion/test-conexion')
};

export const notasCreditoAPI = {
  getAll: (params) => api.get('/notas-credito', { params }),
  generar: (ventaId, data) => api.post(`/notas-credito/generar/${ventaId}`, data),
  getDevoluciones: (ncId) => api.get(`/notas-credito/${ncId}/devoluciones`),
  completarDevolucion: (devolucionId, data) => api.post(`/notas-credito/devoluciones/${devolucionId}/completar`, data)
};

// ─── Fase 6: Pagos Profesionales (Payphone, Medianet, Tickets) ───

export const pagosAPI = {
  // Payphone
  payphoneConfig: () => api.get('/pagos/payphone/config'),
  payphonePrepare: (data) => api.post('/pagos/payphone/prepare', data),
  payphoneConfirm: (data) => api.post('/pagos/payphone/confirm', data),
  // Medianet
  medianetConfig: () => api.get('/pagos/medianet/config'),
  medianetRegistrar: (data) => api.post('/pagos/medianet/registrar', data),
  // Ticket de venta
  getTicket: (ventaId) => api.get(`/pagos/ticket/${ventaId}`),
  // Transacciones de pago
  getTransacciones: (ventaId) => api.get(`/pagos/transacciones/${ventaId}`)
};

// ─── Fase 7: Cuentas por Cobrar ───

export const cuentasPorCobrarAPI = {
  getAll: (params) => api.get('/cuentas-por-cobrar', { params }),
  getResumen: () => api.get('/cuentas-por-cobrar/resumen'),
  getAbonos: (ventaId) => api.get(`/cuentas-por-cobrar/${ventaId}/abonos`),
  getSaldoCliente: (clienteId) => api.get(`/cuentas-por-cobrar/cliente/${clienteId}/saldo`)
};

// ─── Fase 8: Gastos / Egresos / Caja Chica ───

export const gastosAPI = {
  // Gastos CRUD
  getAll: (params) => api.get('/gastos', { params }),
  getById: (id) => api.get(`/gastos/${id}`),
  create: (data) => api.post('/gastos', data),
  update: (id, data) => api.put(`/gastos/${id}`, data),
  aprobar: (id) => api.post(`/gastos/${id}/aprobar`),
  pagar: (id) => api.post(`/gastos/${id}/pagar`),
  anular: (id, data) => api.post(`/gastos/${id}/anular`, data),
  // Categorías
  getCategorias: () => api.get('/gastos/categorias'),
  createCategoria: (data) => api.post('/gastos/categorias', data),
  updateCategoria: (id, data) => api.put(`/gastos/categorias/${id}`, data),
  // Caja Chica
  getCajaChica: () => api.get('/gastos/caja-chica'),
  abrirCajaChica: (data) => api.post('/gastos/caja-chica/abrir', data),
  reponerCajaChica: (data) => api.post('/gastos/caja-chica/reponer', data),
  cerrarCajaChica: (data) => api.post('/gastos/caja-chica/cerrar', data),
  reabrirCajaChica: () => api.post('/gastos/caja-chica/reabrir'),
  // Resumen
  getResumen: (params) => api.get('/gastos/resumen', { params }),
  // Comprobantes (anti-fraude)
  getComprobantes: (id) => api.get(`/gastos/${id}/comprobantes`),
  uploadComprobantes: (id, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('comprobantes', f));
    return api.post(`/gastos/${id}/comprobantes`, formData);
  },
  deleteComprobante: (id, comprobanteId) => api.delete(`/gastos/${id}/comprobantes/${comprobanteId}`)
};

// ─── Gastos diarios (mig 048) — separado de caja chica ───

export const gastosDiariosAPI = {
  getAll: (params) => api.get('/gastos-diarios', { params }),
  getById: (id) => api.get(`/gastos-diarios/${id}`),
  getCategorias: () => api.get('/gastos-diarios/categorias'),
  // datos: { fecha, concepto, ... } + files: File[] (comprobantes, 0..5)
  create: (datos, files) => {
    const formData = new FormData();
    Object.entries(datos).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') formData.append(k, v);
    });
    (files || []).forEach(f => formData.append('comprobantes', f));
    return api.post('/gastos-diarios', formData);
  },
  agregarComprobantes: (id, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('comprobantes', f));
    return api.post(`/gastos-diarios/${id}/comprobantes`, formData);
  },
  // Devuelve un Blob (el archivo se sirve solo con sesión)
  descargarComprobante: (id, comprobanteId) =>
    api.get(`/gastos-diarios/${id}/comprobantes/${comprobanteId}/archivo`, { responseType: 'blob' }),
  anular: (id, motivo) => api.post(`/gastos-diarios/${id}/anular`, { motivo })
};

// ─── Fase 9: Conciliación Bancaria ───

export const conciliacionAPI = {
  // Cuentas Bancarias
  getCuentas: () => api.get('/conciliacion/cuentas-bancarias'),
  createCuenta: (data) => api.post('/conciliacion/cuentas-bancarias', data),
  updateCuenta: (id, data) => api.put(`/conciliacion/cuentas-bancarias/${id}`, data),
  // Períodos
  getPeriodos: (params) => api.get('/conciliacion/periodos', { params }),
  getPeriodoDetalle: (id) => api.get(`/conciliacion/periodos/${id}`),
  crearPeriodo: (data) => api.post('/conciliacion/periodos', data),
  cerrarPeriodo: (id) => api.post(`/conciliacion/periodos/${id}/cerrar`),
  // Importación + Auto-match
  importarEstadoCuenta: (periodoId, file) => {
    const formData = new FormData();
    formData.append('archivo', file);
    return api.post(`/conciliacion/periodos/${periodoId}/importar`, formData);
  },
  ejecutarAutoMatch: (periodoId) => api.post(`/conciliacion/periodos/${periodoId}/auto-match`),
  // Líneas
  getLineas: (periodoId, params) => api.get(`/conciliacion/periodos/${periodoId}/lineas`, { params }),
  buscarRegistros: (periodoId, params) => api.get(`/conciliacion/periodos/${periodoId}/buscar-registros`, { params }),
  matchManual: (lineaId, data) => api.post(`/conciliacion/lineas/${lineaId}/match`, data),
  unmatch: (lineaId) => api.delete(`/conciliacion/lineas/${lineaId}/match`),
  ignorarLinea: (lineaId) => api.post(`/conciliacion/lineas/${lineaId}/ignorar`),
  // Ajustes
  crearAjuste: (data) => api.post('/conciliacion/ajustes', data),
  // Reglas
  getReglas: () => api.get('/conciliacion/reglas'),
  createRegla: (data) => api.post('/conciliacion/reglas', data),
  updateRegla: (id, data) => api.put(`/conciliacion/reglas/${id}`, data),
  // Resumen
  getResumen: () => api.get('/conciliacion/resumen')
};

// ─── Fase 10: Multi-Media Productos ───

export const productoMediaAPI = {
  getMedia: (productoId) => api.get(`/producto-media/${productoId}`),
  addMedia: (productoId, data) => api.post(`/producto-media/${productoId}`, data),
  deleteMedia: (mediaId) => api.delete(`/producto-media/item/${mediaId}`),
  reorder: (productoId, items) => api.put(`/producto-media/reorder/${productoId}`, { items }),
  setPrincipal: (mediaId) => api.put(`/producto-media/principal/${mediaId}`)
};

// ─── API Keys (Super Admin) ───

export const apiKeysAPI = {
  getAll: () => api.get('/api-keys'),
  getById: (id) => api.get(`/api-keys/${id}`),
  create: (data) => api.post('/api-keys', data),
  update: (id, data) => api.put(`/api-keys/${id}`, data),
  delete: (id) => api.delete(`/api-keys/${id}`),
  regenerar: (id) => api.post(`/api-keys/${id}/regenerar`)
};

// ─── Fase 11: Análisis con IA ───

export const analisisIAAPI = {
  // Config
  getConfig: () => api.get('/analisis-ia/config'),
  updateConfig: (clave, valor) => api.put('/analisis-ia/config', { clave, valor }),
  deleteConfig: (clave) => api.delete(`/analisis-ia/config/${clave}`),
  // Health
  healthCheck: () => api.get('/analisis-ia/health'),
  // Dashboard y Alertas
  getDashboard: () => api.get('/analisis-ia/dashboard'), // NOTE: not currently used in frontend
  getAlertas: () => api.get('/analisis-ia/alertas'),
  // Ejecutar análisis
  ejecutar: (modulo, params, config) => api.post(`/analisis-ia/ejecutar/${modulo}`, { params }, config),
  // Resultados
  getResultados: (params) => api.get('/analisis-ia/resultados', { params }),
  getResultadoById: (id) => api.get(`/analisis-ia/resultados/${id}`), // NOTE: not currently used in frontend
  deleteResultado: (id) => api.delete(`/analisis-ia/resultados/${id}`), // NOTE: not currently used in frontend
  // Exportar
  exportarPDF: (id) => api.get(`/analisis-ia/resultados/${id}/pdf`, { responseType: 'blob' }),
  exportarExcel: (id) => api.get(`/analisis-ia/resultados/${id}/excel`, { responseType: 'blob' }),
  // Email
  enviarEmail: (id) => api.post(`/analisis-ia/resultados/${id}/email`),
  // Chat
  chat: (mensaje, session_id, config) => api.post('/analisis-ia/chat', { mensaje, session_id }, config),
  getChatHistory: (sessionId) => api.get(`/analisis-ia/chat/${sessionId}`), // NOTE: not currently used in frontend
  clearChatHistory: (sessionId) => api.delete(`/analisis-ia/chat/${sessionId}`),
  // Schedule
  getSchedule: () => api.get('/analisis-ia/schedule'),
  updateSchedule: (modulo, data) => api.put(`/analisis-ia/schedule/${modulo}`, data)
};

// FIX: Token refresh race condition guard.
// When multiple concurrent requests get 401, only ONE refresh is attempted.
// Other requests wait for the same refresh promise to resolve.
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
};

const onRefreshFailed = (err) => {
  refreshSubscribers.forEach(cb => cb(null, err));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // FIX: Handle 401 errors - redirect to login when not recoverable.
    // Only attempt refresh for TOKEN_EXPIRED, not for other 401s (invalid credentials, etc.)
    if (error.response?.status === 401 &&
        error.response?.data?.code === 'TOKEN_EXPIRED' &&
        !originalRequest._retry) {

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // FIX: No refresh token available, clear auth and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sucursalActiva');
        delete api.defaults.headers.common['Authorization'];
        delete api.defaults.headers.common['X-Sucursal-Id'];
        window.location.href = '/login';
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // FIX: If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken, err) => {
            if (err || !newToken) {
              return reject(err || error);
            }
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const response = await api.post('/auth/refresh', { refreshToken });
        if (response.data.success) {
          const { token: newToken, refreshToken: newRefresh } = response.data.data;
          localStorage.setItem('token', newToken);
          localStorage.setItem('refreshToken', newRefresh);
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          isRefreshing = false;
          onRefreshed(newToken);

          return api(originalRequest);
        }
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed(refreshError);

        // FIX: Refresh failed, clear auth and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('sucursalActiva');
        delete api.defaults.headers.common['Authorization'];
        delete api.defaults.headers.common['X-Sucursal-Id'];
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // FIX: For non-TOKEN_EXPIRED 401s (e.g., invalid token, revoked session),
    // clear auth and redirect to login
    if (error.response?.status === 401 && !originalRequest._retry) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sucursalActiva');
      delete api.defaults.headers.common['Authorization'];
      delete api.defaults.headers.common['X-Sucursal-Id'];
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// ─── Fase 5.5: Facturación de Servicios (comprobantes internos NO-SRI) ───

export const serviciosAPI = {
  getAll: (params) => api.get('/servicios', { params }),
  getById: (id) => api.get(`/servicios/${id}`),
  create: (data) => api.post('/servicios', data),
  update: (id, data) => api.put(`/servicios/${id}`, data),
  remove: (id, hard = false) => api.delete(`/servicios/${id}${hard ? '?hard=true' : ''}`)
};

export const atencionesAPI = {
  getAll: (params) => api.get('/atenciones', { params }),
  getById: (id) => api.get(`/atenciones/${id}`),
  create: (data) => api.post('/atenciones', data),
  registrarAbono: (id, data) => api.post(`/atenciones/${id}/abono`, data),
  anular: (id, data) => api.post(`/atenciones/${id}/anular`, data)
};

export const comprobantesAtencionAPI = {
  getById: (id) => api.get(`/comprobantes-atencion/${id}`),
  descargarPDF: (id) => api.get(`/comprobantes-atencion/${id}/pdf`, { responseType: 'blob' }),
  descargarTicket: (id) => api.get(`/comprobantes-atencion/${id}/ticket`, { responseType: 'blob' }),
  enviarEmail: (id, data) => api.post(`/comprobantes-atencion/${id}/email`, data),
  getPdfUrl: (id) => {
    // Helper: URL directa al PDF (requiere token en header, usar fetch o window.open con proxy)
    return `${API_BASE}/comprobantes-atencion/${id}/pdf`;
  }
};

export const certificadosAPI = {
  getAll: (params) => api.get('/certificados', { params }),
  getBorrador: (params) => api.get('/certificados/borrador', { params }),
  getById: (id) => api.get(`/certificados/${id}`),
  create: (data) => api.post('/certificados', data),
  update: (id, data) => api.put(`/certificados/${id}`, data),
  descargarPDF: (id) => api.get(`/certificados/${id}/pdf`, { responseType: 'blob' }),
  enviarEmail: (id, data) => api.post(`/certificados/${id}/email`, data),
  anular: (id, data) => api.post(`/certificados/${id}/anular`, data),
  getPlantillas: () => api.get('/certificados/plantillas'),
  updatePlantilla: (tipo, data) => api.put(`/certificados/plantillas/${tipo}`, data),
  updateConfigEmisor: (data) => api.put('/certificados/config-emisor', data)
};

export default api;
